import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Loader } from 'lucide-react';
import { webPushManager } from '../../Components/Utils/webPush';
import './WebPushButton.css';

interface WebPushButtonProps {
  className?: string;
  showText?: boolean;
  onSubscriptionChange?: (isSubscribed: boolean) => void;
}

const WebPushButton: React.FC<WebPushButtonProps> = ({ 
  className = '', 
  showText = true,
  onSubscriptionChange 
}) => {
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSupported, setIsSupported] = useState<boolean>(false);

  useEffect(() => {
    checkSupport();
    checkSubscriptionStatus();
  }, []);

  const checkSupport = () => {
    const supported = webPushManager.isSupported();
    setIsSupported(supported);
    
    if (!supported) {
      console.warn('Web Push Notifications are not supported in this browser');
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      const status = await webPushManager.getSubscriptionStatus();
      setIsSubscribed(status.isSubscribed);
      setPermission(status.permission);
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  const handleSubscribe = async () => {
    setIsLoading(true);
    
    try {
      const success = await webPushManager.setupPushNotifications();
      
      if (success) {
        setIsSubscribed(true);
        setPermission('granted');
        onSubscriptionChange?.(true);
        
        // Show success message
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('DateNow Notifications Enabled', {
            body: 'You will now receive notifications about matches and messages!',
            icon: '/favicon.ico',
            tag: 'subscription-success'
          });
        }
      } else {
        throw new Error('Failed to setup push notifications');
      }
    } catch (error) {
      console.error('Subscription failed:', error);
      
      let errorMessage = 'Failed to enable notifications. ';
      
      if (error instanceof Error) {
        if (error.message.includes('permission denied')) {
          errorMessage += 'Please allow notifications in your browser settings.';
        } else if (error.message.includes('not authenticated')) {
          errorMessage += 'Please log in to enable notifications.';
        } else {
          errorMessage += 'Please try again later.';
        }
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setIsLoading(true);
    
    try {
      const success = await webPushManager.unsubscribe();
      
      if (success) {
        setIsSubscribed(false);
        onSubscriptionChange?.(false);
        alert('You have been unsubscribed from notifications.');
      } else {
        throw new Error('Failed to unsubscribe');
      }
    } catch (error) {
      console.error('Unsubscribe failed:', error);
      alert('Failed to unsubscribe. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    if (isSubscribed) {
      handleUnsubscribe();
    } else {
      handleSubscribe();
    }
  };

  // Don't render if not supported
  if (!isSupported) {
    return null;
  }

  // Don't render if permission is denied
  if (permission === 'denied') {
    return showText ? (
      <div className={`web-push-button disabled ${className}`}>
        <BellOff size={20} />
        {showText && <span>Notifications Blocked</span>}
      </div>
    ) : null;
  }

  return (
    <button
      className={`web-push-button ${isSubscribed ? 'subscribed' : 'unsubscribed'} ${className}`}
      onClick={handleClick}
      disabled={isLoading}
      title={isSubscribed ? 'Disable notifications' : 'Enable notifications'}
    >
      {isLoading ? (
        <Loader size={20} className="spinning" />
      ) : isSubscribed ? (
        <Bell size={20} />
      ) : (
        <BellOff size={20} />
      )}
      
      {showText && (
        <span>
          {isLoading 
            ? 'Processing...' 
            : isSubscribed 
              ? 'Notifications On' 
              : 'Enable Notifications'
          }
        </span>
      )}
    </button>
  );
};

export default WebPushButton;